import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Float32BufferAttribute, Group, type Line } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { focusCadTipInput } from '@/plugins/cad/module/utils/focusCadTipInput'
import { styleCadTipNumberInput } from '@/plugins/cad/module/utils/styleCadTipNumberInput'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawEntityCommand } from '../command/DrawEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import { type IPolylineVertice, PolylineEntity } from '../model/entity'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import { MaterialUtils } from '../object/material/MaterialUtils'
import type { EntitySelector } from '../selector/entitySelector'

interface IPolygonParams {
  vertices: IPolylineVertice[]
}

const MIN_POLYGON_EDGE_COUNT = 3
const MAX_POLYGON_EDGE_COUNT = 1024

const polygonMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
    entryInputEdgeNum: () => {},
    exitInputEdgeNum: () => {},
  },
}).createMachine({
  id: 'drawPolygonTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'draw.inputEdgeNum' }],
      },
    },
    draw: {
      initial: 'inputEdgeNum',
      states: {
        // 输入边数
        inputEdgeNum: {
          entry: 'entryInputEdgeNum',
          exit: 'exitInputEdgeNum',
          on: { NEXT: 'center' },
        },
        // 中心点
        center: {
          entry: { type: 'showMouseTip', params: CadTipKey.draw.polygon.center },
          on: { NEXT: 'radius' },
        },
        // 半径
        radius: {
          entry: { type: 'showMouseTip', params: CadTipKey.draw.polygon.circumscribedRadius },
          on: { NEXT: '#drawPolygonTool.finished' },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 多边形绘制工具，通过输入边数后拾取中心与半径生成正多边形。
 */
@injectable()
export class DrawPolygonTool extends StateMachineTool<typeof polygonMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawPolygonTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = polygonMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
      entryInputEdgeNum: () => this.entryInputEdgeNum(),
      exitInputEdgeNum: () => this.exitInputEdgeNum(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private lineObject: Line2 | null = null
  private dimensionGroup: LineDimensionGroup | null = null
  private previewLineObject: Line | null = null
  private entity: PolylineEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private polygonParams: IPolygonParams | null = null
  private edgeNum: number = 3
  private edgeNumInput: HTMLInputElement | null = null
  private onDocumentMouseDown: ((event: MouseEvent) => void) | null = null

  protected onInitialize(entityData?: Partial<PolylineEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      undefined,
      this.isCanCommit.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.lineObject && DrawUtils.disposeObject(this.lineObject)
    this.dimensionGroup?.dispose()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)
    if (this.onDocumentMouseDown) {
      document.removeEventListener('mousedown', this.onDocumentMouseDown, false)
      this.onDocumentMouseDown = null
    }

    this.points = []
    this.tempPoint = null
    this.lineObject = null
    this.dimensionGroup = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
    this.polygonParams = null
    this.edgeNum = 3
    this.edgeNumInput = null
  }

  /**
   * 鼠标按下：左键确认中心/半径点，右键在输入边数阶段等价于确认。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
      }
    } else if (params.event.button === 2) {
      // 右键
      if (this.checkState('draw.inputEdgeNum')) {
        // 在输入边数阶段右键相当于确认边数，进入下一步
        this.confirmEdgeNum()
      }
    }
  }

  /**
   * 鼠标移动：更新中心/半径预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (!this.checkState('draw.center') && !this.checkState('draw.radius')) {
      return
    }
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出，ENTER 在输入边数阶段提交边数。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    } else if (params.event.keyCode === KeyCodes.ENTER) {
      if (this.checkState('draw.inputEdgeNum')) {
        // 在输入边数阶段按下回车相当于确认边数，进入下一步
        this.confirmEdgeNum()
      }
    }
  }

  /** 每帧刷新多边形标注 */
  onViewerUpdate() {
    this.dimensionGroup?.update()
  }

  // 正交捕捉处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points[this.points.length - 1]
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  private entryInputEdgeNum() {
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.removeDocumentMouseDownListener()
    this.mouseTipHelper.addCustomTip(MouseTipId.Draw, (translate) =>
      this.createEdgeNumTipElement(translate)
    )
    if (this.edgeNumInput) {
      focusCadTipInput(this.edgeNumInput, true)
    }
  }

  private createEdgeNumTipElement(translate: (key: string) => string) {
    const element = document.createElement('span')
    element.innerText = translate(CadTipKey.draw.polygon.edgeCountLabel)
    element.style = 'background-color: #fff; color: black; padding: 4px; font-size: 12px;'
    const inputElement = document.createElement('input')
    inputElement.type = 'text'
    inputElement.inputMode = 'numeric'
    inputElement.pattern = '[0-9]*'
    inputElement.value = String(this.edgeNum)
    styleCadTipNumberInput(inputElement)
    inputElement.addEventListener('beforeinput', (event) => {
      if (event.data !== null && !/^\d+$/.test(event.data)) {
        event.preventDefault()
      }
    })
    inputElement.addEventListener('input', () => this.updateEdgeNumFromInput(false))
    inputElement.addEventListener('change', () => this.updateEdgeNumFromInput(true))
    inputElement.addEventListener('mousedown', (e) => {
      if (e.button === 2) {
        e.stopPropagation()
        e.preventDefault()
        this.confirmEdgeNum()
      }
    })
    this.removeDocumentMouseDownListener()
    this.onDocumentMouseDown = (event: MouseEvent) => {
      // 检查点击的元素是否不是 input 本身, 如果不是，则阻止默认行为并聚焦到 input 上
      if (event.target !== inputElement) {
        event.preventDefault()
        focusCadTipInput(inputElement, true)
      }
    }
    document.addEventListener('mousedown', this.onDocumentMouseDown, false)
    element.appendChild(inputElement)
    this.edgeNumInput = inputElement
    return element
  }

  private updateEdgeNumFromInput(finalize: boolean) {
    if (!this.edgeNumInput) {
      return
    }
    const rawValue = this.edgeNumInput.value
    if (rawValue === '' && !finalize) {
      return
    }
    if (!/^\d+$/.test(rawValue)) {
      this.edgeNumInput.value = String(this.edgeNum)
      return
    }
    const value = Number(rawValue)
    if (!Number.isSafeInteger(value)) {
      this.edgeNumInput.value = String(this.edgeNum)
      return
    }
    if (value > MAX_POLYGON_EDGE_COUNT) {
      this.edgeNum = MAX_POLYGON_EDGE_COUNT
      this.edgeNumInput.value = String(this.edgeNum)
      return
    }
    if (value < MIN_POLYGON_EDGE_COUNT) {
      if (finalize) {
        this.edgeNum = MIN_POLYGON_EDGE_COUNT
        this.edgeNumInput.value = String(this.edgeNum)
      }
      return
    }
    this.edgeNum = value
  }

  private confirmEdgeNum() {
    this.updateEdgeNumFromInput(true)
    this.send({ type: 'NEXT' })
  }

  private exitInputEdgeNum() {
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.removeDocumentMouseDownListener()
    this.edgeNumInput = null
  }

  private removeDocumentMouseDownListener() {
    if (this.onDocumentMouseDown) {
      document.removeEventListener('mousedown', this.onDocumentMouseDown, false)
      this.onDocumentMouseDown = null
    }
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }
    const p1 = points[points.length - 2]
    const p2 = points[points.length - 1]
    // 绘制辅助预览线
    this.updatePreviewLine(p1, p2)

    // 绘制标注
    this.updateDimensionGroup(p1, p2)

    // 计算多边形参数
    const polygonParams = this.getPolygonParams(p1, p2, this.edgeNum)
    this.updateLineObject(polygonParams)
    this.polygonParams = polygonParams
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(20)
      const materialOption = {
        color: Setting.previewLineConfig.color,
        transparent: Setting.previewLineConfig.opacity < 1,
        opacity: Setting.previewLineConfig.opacity,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedLine(p1, p2, materialOption, this.group)
      return
    }

    const positions = [p1, p2].flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  private updateDimensionGroup(startPoint: IPoint, endPoint: IPoint) {
    if (this.dimensionGroup) {
      this.dimensionGroup.updateLine(startPoint, endPoint)
      return
    }
    this.dimensionGroup = new LineDimensionGroup(
      startPoint,
      endPoint,
      {
        showAngle: false,
      },
      this.group,
      this.controller.domElement!.parentElement!,
      (point: IPoint) => this.controller.getScreenPosition(point),
      (radius: number) => this.controller.getPixelSizeInWorld(radius),
      this.unitPresentation
    )
  }

  private updateLineObject(params: IPolygonParams | null) {
    if (!params || !this.entity) {
      this.lineObject && (this.lineObject.visible = false)
      return
    }
    const points = [...params.vertices, params.vertices[0]].map((v) => ({ x: v.x, y: v.y }))
    if (!this.lineObject) {
      const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
      const material = MaterialUtils.createMaterialByLineType(this.entity.lineType, materialOption)
      const geometry = DrawUtils.getLineGeometryByPoints(points)
      this.lineObject = createCadLine2Object(geometry, material)
      this.group.add(this.lineObject)
    } else {
      this.lineObject.geometry.setPositions(points.flatMap((p) => [p.x, p.y, 0]))
      this.lineObject.geometry.attributes.position.needsUpdate = true
    }
    this.lineObject.computeLineDistances()
    this.lineObject.visible = true
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<PolylineEntity>) {
    const entity = new PolylineEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      isClosed: true,
      id: nanoid(),
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.polygonParams) {
      Object.assign(this.entity, { ...this.polygonParams })
    }
  }

  private isCanCommit(): boolean {
    return this.entity ? this.entity.vertices.length > 2 : false
  }

  private getPolygonParams(p1: IPoint, p2: IPoint, edgeNum: number) {
    const vertices: IPolylineVertice[] = []
    const center = { x: p1.x, y: p1.y }
    const radius = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2)
    if (edgeNum < 3 || radius <= 0) {
      return { vertices }
    }
    // 根据中心点、外切圆半径和边数计算多边形顶点坐标
    // 以 p2 方向为起始角，确保 p2 在顶点上
    const step = (Math.PI * 2) / edgeNum
    const startAngle = Math.atan2(p2.y - center.y, p2.x - center.x)
    for (let i = 0; i < edgeNum; i++) {
      const angle = startAngle + step * i
      vertices.push({
        x: center.x + Math.cos(angle) * radius,
        y: center.y + Math.sin(angle) * radius,
        bulge: 0,
      })
    }
    return { vertices }
  }
}
